import { pubText, pubPath, pubRich } from "../../i18n/publicTranslation";
import { ArrowDownwardRounded, ArrowForwardRounded } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { GAMBIT_PAGE_PATH, HARNESS_ARTICLE_PATH } from '../../data/gambitMarketing';
import HomeSectionDivider from '../common/HomeSectionDivider';
import './GambitMarketing.css';
import GambitStageVisual from './GambitStageVisual';
import GoOriginalArtwork from './GoOriginalArtwork';

const GOLD_LOGO = '/fable-butterfly-gold.png?v=butterfly-20260916';
const WHITE_LOGO = '/images/gambit/butterfly-white.svg';

const goSteps = [
  {
    title: 'Choose your character',
    text: 'Dream up someone new or put yourself in the story. Add photos to create a character who looks like you.',
    visual: 'character',
    visualLabel:
      'Personalize Leo in Fable’s character editor using photos of the same boy in different settings',
  },
  {
    title: 'Tell us your idea',
    text: 'A quick bedtime adventure or a world of your own. Describe the story you want to tell.',
    visual: 'idea',
    visualLabel:
      'Tonight, Leo faces his greatest rival, with Leo selected as the story’s character',
  },
  {
    title: 'Enjoy your story',
    text: 'Let Fable bring it to life with illustrations and narration. Choose a narrator, design a new voice or use your own.',
    visual: 'story',
    visualLabel:
      'Leo’s photo becomes an anime superhero facing his greatest rival across rain-soaked city rooftops',
  },
] as const;

const gambitSteps = [
  {
    stage: 'blueprint',
    title: 'Plan your story',
    text: 'Talk through your idea and develop the characters, plot and direction in a blueprint.',
    alt: 'Gambit blueprint workspace with a story plan, narrative arc and character references',
  },
  {
    stage: 'script',
    title: 'Write and refine your script',
    text: 'Work on the dialogue, pacing and details. Ask for changes and review the script as it develops.',
    alt: 'Gambit script workspace with character dialogue, delivery notes and sound cues',
  },
  {
    stage: 'audio',
    title: 'Choose voices and add sound',
    text: 'Find a voice for each character, design a new one or use your own. Then shape the music, sound effects and delivery.',
    alt: 'Gambit audio workspace showing character voices and the sound plan',
  },
  {
    stage: 'storyboard',
    title: 'Build your storyboard',
    text: 'Bring your cast and world into each scene. Refine the pictures and prepare your story for video.',
    alt: 'Gambit storyboard workspace with a scene plan and illustrated shots',
  },
] as const;

function Brand({ mode }: { mode: 'go' | 'gambit' }) {
  return (
    <>
      <img
        className={`creation-logo creation-logo--${mode}`}
        src={mode === 'go' ? GOLD_LOGO : WHITE_LOGO}
        alt={pubText("")}
        width={mode === 'go' ? 56 : 32}
        height={mode === 'go' ? 56 : 32}
      />
      <span>{pubText(mode === 'go' ? 'Go' : 'Gambit')}</span>
    </>
  );
}

export default function StoryCreationWays() {
  return (
    <section
      className="gambit-marketing fable-creation-ways"
      id="how-it-works"
      aria-labelledby="creation-ways-title"
    >
      <div className="gambit-section-intro">
        <h2 id="creation-ways-title">{pubRich("How __SLOT_0__ works", [<span>{pubText("Fable")}</span>])}</h2>
        <p>{pubText(
          " Fable gives you two ways to create. Share an idea and let us bring it to life with Go, or take the lead with Gambit and shape every detail. "
        )}</p>
        <nav className="creation-jump-links" aria-label={pubText("Explore ways to create")}>
          <a href={pubPath("#fable-go-steps")}>
            <Brand mode="go" />
            <ArrowDownwardRounded />
          </a>
          <a href={pubPath("#fable-gambit-steps")}>
            <Brand mode="gambit" />
            <ArrowDownwardRounded />
          </a>
        </nav>
      </div>
      <section
        className="creation-walkthrough creation-walkthrough--go"
        id="fable-go-steps"
        aria-labelledby="go-walkthrough-title"
      >
        <HomeSectionDivider spaceBefore />
        <div className="creation-walkthrough-heading">
          <div>
            <h3 id="go-walkthrough-title" className="creation-brand">
              <Brand mode="go" />
            </h3>
            <p>{pubText("Choose a character. Share an idea. Let Fable do the storytelling.")}</p>
          </div>
        </div>
        <ol className="creation-go-grid">
          {pubText(goSteps.map((step, index) => (
            <li key={step.visual} id={`fable-go-step-${index + 1}`}>
              <div className={`creation-go-art creation-go-art--${step.visual}`}>
                <GoOriginalArtwork variant={step.visual} label={pubText(step.visualLabel)} />
              </div>
              <div className="creation-step-copy">
                <span className="creation-step-number">{pubText("STEP 0")}{pubText(index + 1)}</span>
                <h4>{pubText(step.title)}</h4>
                <p>{pubText(step.text)}</p>
              </div>
            </li>
          )))}
        </ol>
        <div className="creation-walkthrough-actions">
          <Link to={pubPath("/create?mode=go")} className="gambit-button gambit-button--go">{pubRich(" Create with Go __SLOT_0__ ", [<ArrowForwardRounded />])}</Link>
        </div>
      </section>
      <section
        className="creation-walkthrough creation-walkthrough--gambit"
        id="fable-gambit-steps"
        aria-labelledby="gambit-walkthrough-title"
      >
        <HomeSectionDivider spaceBefore />
        <div className="creation-walkthrough-heading">
          <div>
            <h3 id="gambit-walkthrough-title" className="creation-brand">
              <Brand mode="gambit" />
            </h3>
            <p>{pubText(
              "Build your story through conversation, with room to revise at every stage."
            )}</p>
          </div>
        </div>
        <ol className="creation-gambit-grid">
          {pubText(gambitSteps.map((step, index) => (
            <li key={step.stage}>
              <GambitStageVisual stage={step.stage} alt={pubText(step.alt)} />
              <div className="creation-step-copy">
                <span className="creation-step-number">{pubText("STEP 0")}{pubText(index + 1)}</span>
                <h4>{pubText(step.title)}</h4>
                <p>{pubText(step.text)}</p>
                {pubText(step.stage === 'audio' && (
                  <Link to={pubPath("/narrators")} className="gambit-text-link creation-voice-link">{pubRich(" Explore voices __SLOT_0__ ", [<ArrowForwardRounded />])}</Link>
                ))}
              </div>
            </li>
          )))}
        </ol>
        <div className="creation-walkthrough-actions">
          <Link to={pubPath(GAMBIT_PAGE_PATH)} className="gambit-button">{pubRich(" Explore Gambit __SLOT_0__ ", [<ArrowForwardRounded />])}</Link>
          <Link to={pubPath(HARNESS_ARTICLE_PATH)} className="gambit-text-link">{pubRich(
              " How Gambit works behind the scenes __SLOT_0__ ",
              [<ArrowForwardRounded />]
            )}</Link>
        </div>
      </section>
    </section>
  );
}
